import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { isNullOrUndefined } from '@/helpers/objectHelpers';
import { COLORS } from '@/styles/colors';
import { AuditEventDetailsType } from '@/types/auditLogTypes';
import isObject from 'lodash/isObject';
import { DiffTable, Mono } from '../styles';

type DiffType = Record<string, { from: unknown; to: unknown }>;

const renderValue = (value: unknown, depth = 0) => {
  if (isObject(value)) {
    const entries = Object.entries(value);

    if (entries.length === 0) {
      return '-';
    }

    return (
      <Box
        display='flex'
        flexDirection='column'
        gap='3px'
        marginLeft={depth > 0 ? '12px' : 0}
        paddingLeft={depth > 0 ? '12px' : 0}
        borderLeft={depth > 0 ? `3px solid ${COLORS.grey[350]}` : undefined}
      >
        {entries.map(([key, rowValue]) => (
          <div key={key}>
            <Typography as='span' variant='bodySmall' color={COLORS.grey[650]}>
              {key}:{' '}
            </Typography>
            {isObject(rowValue) ? (
              <Box marginTop='3px'>{renderValue(rowValue, depth + 1)}</Box>
            ) : (
              <Typography as='span' variant='bodySmall'>
                {isNullOrUndefined(rowValue) || rowValue === '' ? '-' : String(rowValue)}
              </Typography>
            )}
          </div>
        ))}
      </Box>
    );
  }

  return isNullOrUndefined(value) || value === '' ? '-' : String(value);
};

type AuditEventChangedFieldsType = {
  details: AuditEventDetailsType;
};

export const AuditEventChangedFields = ({ details }: AuditEventChangedFieldsType) => {
  const diff = (details?.data?.diff as DiffType | undefined) ?? null;
  const extra = details ? Object.entries(details.data).filter(([key]) => key !== 'diff') : [];

  return (
    <>
      {diff && Object.keys(diff).length > 0 && (
        <Box marginTop='16px'>
          <Typography fontWeight='semibold' marginBottom='8px'>
            Changed fields
          </Typography>
          <DiffTable>
            <thead>
              <tr>
                <th>Field</th>
                <th>From</th>
                <th>To</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(diff).map(([field, change]) => (
                <tr key={field}>
                  <td>
                    <Mono>{field}</Mono>
                  </td>
                  <td>{renderValue(change.from)}</td>
                  <td>{renderValue(change.to)}</td>
                </tr>
              ))}
            </tbody>
          </DiffTable>
          <Typography variant='caption' marginTop='8px'>
            Sensitive values are stored masked (***). Account numbers never enter the audit log in clear.
          </Typography>
        </Box>
      )}

      {extra.length > 0 && (
        <Box marginTop='16px'>
          <Typography fontWeight='semibold' marginBottom='8px'>
            Data
          </Typography>
          <DiffTable>
            <tbody>
              {extra.map(([key, value]) => (
                <tr key={key}>
                  <td>
                    <Mono>{key}</Mono>
                  </td>
                  <td>{renderValue(value)}</td>
                </tr>
              ))}
            </tbody>
          </DiffTable>
        </Box>
      )}
    </>
  );
};
